import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useProgramme from './useProgramme';
import LibraryBrowser from './LibraryBrowser';
import ItemList from './ItemList';
import ItemEditor from './ItemEditor';
import BookletView from './BookletView';
import RightsAlertsPanel from './RightsAlertsPanel';
import { scanRightsAlerts } from './rightsAlerts';
import './programme.css';

const SAVE_LABEL = { saved: 'Saved', saving: 'Saving…', error: 'Save failed' };
const input = 'border border-brand-dark rounded-lg px-3 py-2 text-sm w-full';

const Header = ({ programme, update, saveState, onCoverChange, coverBusy }) => {
  const editableStatus = programme.status === 'draft' || programme.status === 'published';
  const nextStatus = programme.status === 'published' ? 'draft' : 'published';
  const actionLabel = programme.status === 'published' ? 'Unpublish' : 'Publish programme';

  return (
    <div className="bg-white border border-brand-dark rounded-2xl p-4 mb-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <input value={programme.title} onChange={event => update({ title: event.target.value })}
          className="font-serif text-xl font-bold text-accent-navy bg-transparent border-b border-transparent focus:border-accent-gold outline-none" />
        <span className={`text-xs font-bold ${saveState === 'error' ? 'text-red-600' : 'text-text-muted'}`}>{SAVE_LABEL[saveState]}</span>
      </div>
      <div className="grid md:grid-cols-3 gap-2">
        <input className={input} placeholder="Name of the deceased" value={programme.deceasedName} onChange={event => update({ deceasedName: event.target.value })} />
        <input className={input} placeholder="Dates (e.g. 1950 – 2024)" value={programme.dates} onChange={event => update({ dates: event.target.value })} />
        <input className={input} placeholder="Subtitle" value={programme.subtitle} onChange={event => update({ subtitle: event.target.value })} />
        <label className="text-xs font-bold text-accent-navy">
          Cover image
          <input disabled={coverBusy} type="file" accept="image/jpeg,image/png,image/webp" onChange={onCoverChange} className="block mt-1 w-full text-xs" />
        </label>
        <textarea rows={1} className={`${input} md:col-span-2`} placeholder="Service details (venue, time)" value={programme.details} onChange={event => update({ details: event.target.value })} />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="text-xs font-bold uppercase tracking-widest text-accent-navy">
          Audio
          <select value={programme.audioMode} onChange={event => update({ audioMode: event.target.value })} className="ml-2 border border-brand-dark rounded-lg px-2 py-1 text-sm normal-case">
            <option value="off">Off (guests see no player)</option>
            <option value="staff">Staff only</option>
          </select>
        </label>
        <span className="text-xs font-bold uppercase tracking-widest text-accent-navy">
          Status: <span className="normal-case">{programme.status || 'draft'}</span>
        </span>
        {editableStatus && (
          <button type="button" onClick={() => update({ status: nextStatus })}
            className="px-4 py-2 rounded-lg text-white text-sm font-bold bg-accent-navy">
            {actionLabel}
          </button>
        )}
        {programme.status === 'published' && programme.shareSlug && (
          <Link to={`/programme/${programme.shareSlug}`} className="text-sm font-bold text-accent-gold underline">Open reader view</Link>
        )}
      </div>
    </div>
  );
};

const ProgrammeEditor = () => {
  const { id } = useParams();
  const p = useProgramme(id);
  const [selectedId, setSelectedId] = useState(null);
  const [uploadError, setUploadError] = useState('');

  if (p.loading) return <div className="min-h-screen pt-28 text-center text-text-muted">Loading programme…</div>;
  if (p.error) return <div className="min-h-screen pt-28 text-center text-red-600">{p.error}</div>;
  if (!p.programme) return <div className="min-h-screen pt-28 text-center text-text-muted">Programme not found.</div>;

  const item = p.items.find(entry => entry.id === selectedId);
  const piece = p.library.find(entry => entry.id === item?.pieceId);
  const alerts = scanRightsAlerts(p.alerts);

  const onCoverChange = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadError('');
    const result = await p.uploadCover(file);
    if (!result) setUploadError('Cover image upload failed. Check the file type, size, and your programme permissions.');
  };

  return (
    <div className="min-h-screen bg-brand-light pt-24 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Header programme={p.programme} update={p.updateProgramme} saveState={p.saveState}
          onCoverChange={onCoverChange} coverBusy={p.saveState === 'saving'} />
        {uploadError && <p role="alert" className="mb-4 text-sm text-red-600">{uploadError}</p>}
        {alerts.length > 0 && <RightsAlertsPanel alerts={alerts} onSelectItem={setSelectedId} defaultOpen />}
        <div className="grid lg:grid-cols-3 gap-4">
          <LibraryBrowser library={p.library} onAdd={p.addItem} />
          <div className="space-y-4">
            <ItemList items={p.items} selectedId={selectedId} onSelect={setSelectedId} onMove={p.moveItem}
              onRemove={p.removeItem} onAdd={p.addItem} />
            <ItemEditor item={item} piece={piece} onChange={patch => item && p.updateItem(item.id, patch)} />
          </div>
          <div className="max-h-[85vh] overflow-y-auto">
            <BookletView programme={p.programme} items={p.items} library={p.library} showAudio={false} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgrammeEditor;